import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { backtestAPI } from '../../api/backtests';

// Async thunks
export const fetchBacktests = createAsyncThunk(
  'backtests/fetchBacktests',
  async (params, { rejectWithValue }) => {
    try {
      const response = await backtestAPI.getBacktests(params);
      return { 
        ...response.data, 
        append: params?.append || false,
        pageDirection: params?.pageDirection,
        currentKey: params?.currentKey
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch backtests');
    }
  }
);

export const fetchBacktestResults = createAsyncThunk(
  'backtests/fetchBacktestResults',
  async (requestId, { rejectWithValue }) => {
    try {
      const response = await backtestAPI.getBacktestResults(requestId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch backtest results');
    }
  }
);

export const createBacktest = createAsyncThunk(
  'backtests/createBacktest',
  async (backtestData, { rejectWithValue }) => {
    try {
      const response = await backtestAPI.createBacktest(backtestData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create backtest');
    }
  }
);

export const deleteBacktest = createAsyncThunk(
  'backtests/deleteBacktest',
  async (requestId, { rejectWithValue }) => {
    try {
      await backtestAPI.deleteBacktest(requestId);
      return requestId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete backtest');
    }
  }
);

export const fetchBacktestSummary = createAsyncThunk(
  'backtests/fetchBacktestSummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await backtestAPI.getBacktestSummary();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch summary');
    }
  }
);

const initialState = {
  // Backtest list
  backtests: [],
  backtestsLoading: false,
  backtestsError: null,
  
  // Current backtest results
  currentBacktest: null,
  currentResults: null,
  resultsLoading: false,
  resultsError: null,
  
  // Summary data
  summary: null,
  summaryLoading: false,
  summaryError: null,
  
  // UI state
  filters: {
    status: '',
    limit: 20
  },
  cursor: null,
  hasMore: false,
  
  // Pagination state
  currentPage: 1,
  pageHistory: [], // Array of cursor values for each page
  
  // Action states
  isCreating: false,
  isDeleting: false,
  createError: null,
  deleteError: null,
  
  // Success messages
  successMessage: null
};

const backtestsSlice = createSlice({
  name: 'backtests',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.cursor = null;
      state.hasMore = false;
      state.currentPage = 1;
      state.pageHistory = [];
    },
    setPage: (state, action) => {
      state.currentPage = action.payload;
    },
    clearCurrentBacktest: (state) => {
      state.currentBacktest = null;
      state.currentResults = null;
      state.resultsError = null;
    },
    clearErrors: (state) => {
      state.backtestsError = null;
      state.resultsError = null;
      state.createError = null;
      state.deleteError = null;
      state.summaryError = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    resetPagination: (state) => {
      state.currentPage = 1;
      state.pageHistory = [];
      state.cursor = null;
      state.hasMore = false;
    },
    resetState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      // Fetch backtests
      .addCase(fetchBacktests.pending, (state) => {
        state.backtestsLoading = true;
        state.backtestsError = null;
      })
      .addCase(fetchBacktests.fulfilled, (state, action) => {
        state.backtestsLoading = false;
        const items = action.payload.data?.items || [];
        const append = action.payload.append;
        const pageDirection = action.payload.pageDirection; // 'next' or 'prev'
        
        state.backtests = append ? [...state.backtests, ...items] : items;
        // Handle cursor-based pagination
        state.cursor = action.payload.data?.cursor || null;
        state.hasMore = state.cursor !== null && state.cursor !== undefined;
        
        // Update pagination state
        if (!append && pageDirection === 'next') {
          // Moving to next page - add current page's cursor to history
          const currentKey = action.payload.currentKey;
          if (currentKey && !state.pageHistory.includes(currentKey)) {
            state.pageHistory.push(currentKey);
          }
          state.currentPage += 1;
        } else if (pageDirection === 'prev' && state.currentPage > 1) {
          // Moving to previous page - remove last cursor from history
          state.pageHistory.pop();
          state.currentPage -= 1;
        } else if (!append && !pageDirection) {
          // Fresh load (filters changed or initial load)
          state.currentPage = 1;
          state.pageHistory = [];
        }
      })
      .addCase(fetchBacktests.rejected, (state, action) => {
        state.backtestsLoading = false;
        state.backtestsError = action.payload;
      })
      
      // Fetch backtest results
      .addCase(fetchBacktestResults.pending, (state) => {
        state.resultsLoading = true;
        state.resultsError = null;
      })
      .addCase(fetchBacktestResults.fulfilled, (state, action) => {
        state.resultsLoading = false;
        state.currentBacktest = action.payload.data?.request;
        state.currentResults = action.payload.data?.results;
      })
      .addCase(fetchBacktestResults.rejected, (state, action) => {
        state.resultsLoading = false;
        state.resultsError = action.payload;
      })
      
      // Create backtest
      .addCase(createBacktest.pending, (state) => {
        state.isCreating = true;
        state.createError = null;
      })
      .addCase(createBacktest.fulfilled, (state, action) => {
        state.isCreating = false;
        state.successMessage = `Backtest "${action.payload.data?.strategyName}" created successfully! It will be processed shortly.`;
        // Add new backtest to the list
        if (action.payload.data) {
          state.backtests.unshift(action.payload.data);
        }
      })
      .addCase(createBacktest.rejected, (state, action) => {
        state.isCreating = false;
        state.createError = action.payload;
      })
      
      // Delete backtest
      .addCase(deleteBacktest.pending, (state) => {
        state.isDeleting = true;
        state.deleteError = null;
      })
      .addCase(deleteBacktest.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.successMessage = 'Backtest deleted successfully';
        // Remove from list
        state.backtests = state.backtests.filter(bt => bt.requestId !== action.payload);
      })
      .addCase(deleteBacktest.rejected, (state, action) => {
        state.isDeleting = false;
        state.deleteError = action.payload;
      })
      
      // Fetch summary
      .addCase(fetchBacktestSummary.pending, (state) => {
        state.summaryLoading = true;
        state.summaryError = null;
      })
      .addCase(fetchBacktestSummary.fulfilled, (state, action) => {
        state.summaryLoading = false;
        state.summary = action.payload.data;
      })
      .addCase(fetchBacktestSummary.rejected, (state, action) => {
        state.summaryLoading = false;
        state.summaryError = action.payload;
      });
  }
});

export const {
  setFilters,
  setPage,
  clearCurrentBacktest,
  clearErrors,
  clearSuccessMessage,
  resetPagination,
  resetState
} = backtestsSlice.actions;

export default backtestsSlice.reducer; 
